/**
 * StreamLain BlockSuite Hooks Registration
 *
 * Registers StreamLain extensions via window globals that BlockSuite checks.
 * This approach avoids direct modifications to BlockSuite files.
 */

import type { GanttWizardStrings } from '@blocksuite/affine/data-view';

/**
 * StreamLain hooks interface.
 * Note: The Window interface is declared in filesys.ts to avoid duplication.
 */
export interface StreamlainHooks {
  /**
   * Additional file types for openFilesWith() in filesys.ts
   */
  fileTypes?: Array<{
    description: string;
    accept: Record<string, string[]>;
  }>;
  /** Replace selected files before a BlockSuite semantic importer reads them. */
  prepareDemoImportFiles?: (input: {
    entry?: string;
    intent?: string;
    workspaceId?: string;
    files: File[];
  }) => Promise<File[]>;
  /**
   * Confirm before deleting multiple rows.
   * Called from BlockSuite Lit layer, implemented by React layer.
   * Returns a Promise that resolves to true (confirmed) or false (cancelled).
   */
  confirmDeleteRows?: (count: number) => Promise<boolean>;
  /**
   * Translated copy for the Gantt setup wizard. Consumed by the Lit
   * wizard component (`dv-gantt-setup-wizard`) via the tenant hook so
   * the data-view package stays free of frontend-i18n dependencies.
   * Any missing key falls back to the English default.
   */
  ganttWizardStrings?: Partial<GanttWizardStrings>;
}

/**
 * Register StreamLain hooks before BlockSuite initializes.
 * Must be called early in app startup.
 */
export function registerStreamlainHooks(hooks: StreamlainHooks): void {
  if (typeof window === 'undefined') return;

  // Cast to extended window type
  const win = window as Window & { __streamlainHooks?: StreamlainHooks };

  // Merge with existing hooks (allows multiple registrations)
  win.__streamlainHooks = {
    ...win.__streamlainHooks,
    ...hooks,
    // fileTypes are additive — concatenate instead of replacing
    fileTypes: [
      ...(win.__streamlainHooks?.fileTypes ?? []),
      ...(hooks.fileTypes ?? []),
    ],
  };
}

/**
 * Get registered StreamLain hooks.
 * Used by BlockSuite integration points.
 */
export function getStreamlainHooks(): StreamlainHooks {
  const win = window as Window & { __streamlainHooks?: StreamlainHooks };
  return win.__streamlainHooks ?? {};
}

type DemoImportHook = NonNullable<StreamlainHooks['prepareDemoImportFiles']>;
const demoImportHooks = new Map<
  symbol,
  { workspaceId: string; hook: DemoImportHook }
>();

// Multiple editors can coexist (for example, a document peek next to the
// workspace editor). Keep every mounted policy active regardless of unmount
// order. Each policy can only replace files or return them unchanged; the
// chain never reads visitor bytes before all policies have made a decision.
const dispatchDemoImport: DemoImportHook = async input => {
  if (demoImportHooks.size === 0) {
    throw new Error('Demo import policy is unavailable');
  }
  if (input.entry && !input.workspaceId) {
    throw new Error('Demo import origin is unavailable');
  }
  if (
    !input.workspaceId &&
    new Set([...demoImportHooks.values()].map(({ workspaceId }) => workspaceId))
      .size > 1
  ) {
    throw new Error('Demo import origin is ambiguous');
  }
  const matchingHooks = [...demoImportHooks.values()].filter(
    registration =>
      !input.workspaceId || registration.workspaceId === input.workspaceId
  );
  // A pending file picker can outlive its editor. Never apply a different
  // workspace's (possibly permissive) policy to the original collection.
  if (matchingHooks.length === 0) {
    throw new Error('Demo import policy for workspace is unavailable');
  }
  let files = input.files;
  for (const { hook } of matchingHooks) {
    files = await hook({ ...input, files });
  }
  return files;
};

export function registerDemoImportHook(
  workspaceId: string,
  hook: DemoImportHook
): () => void {
  const id = Symbol('demo-import-hook');
  demoImportHooks.set(id, { workspaceId, hook });
  registerStreamlainHooks({ prepareDemoImportFiles: dispatchDemoImport });
  return () => {
    demoImportHooks.delete(id);
    if (
      demoImportHooks.size === 0 &&
      getStreamlainHooks().prepareDemoImportFiles === dispatchDemoImport
    ) {
      registerStreamlainHooks({ prepareDemoImportFiles: undefined });
    }
  };
}
